import { CreateStepper } from '@/lib/Steppers';
import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getSmsCampaignSteps, SmsCampaignMode } from './SmsCampaignSteps';
import { TitleDescriptionStep } from './steps/TitleDescriptionStep';
import { SenderSelectionStep } from './steps/SenderSelectionStep';
import { PreviewScheduleStep } from './steps/PreviewScheduleStep';
import { SmsCampaignFormInput, SmsCampaignStep } from './SmsCampaignValidation';
import { AudienceSelectionStep } from './steps/AudienceSelectionStep';
import { useWorkspace } from '@/features/workspaces/hooks';
import ErrorBoundary from '@/components/ErrorBoundary';
import { FlowFormData, FlowMappingsData, StepLevelError } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { runGuardedSmsStepTransition } from './stepGuards';
import { useCreateFlowInstanceMutation, useGetConnectorFlowInstancesQuery, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { createStepReducer, initialStepState } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/stepFlowState';
import { useTranslation } from 'react-i18next';
import RouteConstants from '@/features/router/RouteConstants';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@/reducers';
import { showToast } from '@/features/toasts/toastsSlice';
import { ToastType } from '@/models/toast';
import { useCreateCampaignV2Mutation, useGetCampaignV2Query, useUpdateCampaignV2Mutation } from '@/services/endpoints/campaigns';

type SmsCampaignContentProps = {
  mode?: SmsCampaignMode;
  campaignId?: string;
  onExitToList?: () => void;
};

export const SmsCampaignContent = ({ mode = 'create', campaignId, onExitToList }: SmsCampaignContentProps) => {
  const isEditMode = mode === 'edit';
  const steps = useMemo(() => getSmsCampaignSteps(mode), [mode]);
  const stepReducer = useMemo(() => createStepReducer(steps.length), [steps.length]);
  const [state, dispatch] = useReducer(stepReducer, initialStepState);

  const { watch, trigger, reset } = useFormContext<SmsCampaignFormInput>();
  const currentStep = steps[state.currentStepIndex];
  const formValues = watch();

  const [flowParams, setFlowParams] = useState<FlowFormData>({});
  const [flowMappings, setFlowMappings] = useState<FlowMappingsData>({});
  const [stepError, setStepError] = useState<StepLevelError>(null);
  const { t } = useTranslation('campaign');
  const history = useHistory();
  const appDispatch = useAppDispatch();

  const { id: workspaceId } = useWorkspace();
  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId });
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const [createCampaignV2, { isLoading: isCreatingCampaign }] = useCreateCampaignV2Mutation();
  const [updateCampaignV2, { isLoading: isUpdatingCampaign }] = useUpdateCampaignV2Mutation();

  const {
    data: campaign,
    isLoading: isLoadingCampaign,
    isError: isCampaignError,
  } = useGetCampaignV2Query({ workspaceId, campaignId: campaignId ?? '' }, { skip: !isEditMode || !workspaceId || !campaignId });

  const { data: flowInstances } = useGetConnectorFlowInstancesQuery(
    { workspaceId, audienceId: campaign?.audienceId ?? '' },
    { skip: !isEditMode || !workspaceId || !campaign?.audienceId }
  );

  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || hasHydratedRef.current) return;
    if (!campaign || !instances) return;

    const connectorInstance = instances.find((inst) => inst.id === campaign.connectorInstanceId);
    const flowInstance = flowInstances?.find((fi) => fi.id === campaign.connectorFlowInstanceId);

    if (campaign.connectorFlowInstanceId && !flowInstance) return;

    const isScheduled = Boolean(campaign.scheduledStart);
    reset({
      name: campaign.name ?? '',
      description: campaign.description ?? '',
      templateId: (flowInstance?.params?.['templateId'] as string | undefined) ?? campaign.connectorFlowInstanceId ?? '',
      templateName: (flowInstance?.params?.['templateName'] as string | undefined) ?? undefined,
      audienceId: campaign.audienceId ?? '',
      connectorFlowId: flowInstance?.connectorFlowId ?? '',
      connectorId: connectorInstance?.connectorId ?? '',
      scheduleType: isScheduled ? 'schedule' : 'sendNow',
      scheduleStart: campaign.scheduledStart ? new Date(campaign.scheduledStart) : undefined,
      scheduleEnd: campaign.scheduledEnd ? new Date(campaign.scheduledEnd) : undefined,
    });
    hasHydratedRef.current = true;
  }, [isEditMode, campaign, instances, flowInstances, reset]);

  const stepperSteps = useMemo(() => {
    return steps.map((step, index) => ({
      ...step,
      isCompleted: index < state.currentStepIndex,
    })) as any;
  }, [steps, state.currentStepIndex]);

  const handleNextStep = () => dispatch({ type: 'NEXT_STEP' });
  const handlePreviousStep = () => dispatch({ type: 'PREVIOUS_STEP' });

  const goNextWithGuard = async (stepId: SmsCampaignStep, validate: () => Promise<boolean>) => {
    return runGuardedSmsStepTransition({
      stepId,
      validate,
      onValid: handleNextStep,
      setStepError,
    });
  };

  const handleTitleDescriptionNextStep = async () => goNextWithGuard('TitleDescription', () => trigger(['name', 'description']));
  const handleAudienceNextStep = async () => goNextWithGuard('AudienceSelection', () => trigger('audienceId'));

  const handleSenderNextStep = async (formData: FlowFormData, mappingsData: FlowMappingsData) => {
    setFlowParams(formData);
    setFlowMappings(mappingsData ?? {});
    return goNextWithGuard('SenderSelection' as SmsCampaignStep, async () => true);
  };

  const audienceId = watch('audienceId');
  const connectorFlowId = watch('connectorFlowId');
  const connectorId = watch('connectorId');
  const campaignName = watch('name');
  const campaignDescription = watch('description');

  const exit = () => {
    if (onExitToList) {
      onExitToList();
    } else {
      history.push(RouteConstants.smsCampaigns);
    }
  };

  const buildScheduleIso = () => {
    const scheduledStart = formValues.scheduleType === 'schedule' ? new Date(formValues.scheduleStart as Date).toISOString() : null;
    const scheduledEnd = formValues.scheduleType === 'schedule' ? new Date(formValues.scheduleEnd as Date).toISOString() : null;
    return { scheduledStart, scheduledEnd };
  };

  const handleEditSave = async (sendNow: boolean) => {
    if (!campaignId) {
      return { success: false };
    }
    const { scheduledStart, scheduledEnd } = buildScheduleIso();
    try {
      await updateCampaignV2({
        workspaceId,
        campaignId,
        name: campaignName,
        description: campaignDescription,
        audienceId,
        status: sendNow ? 'ACTIVE' : 'DRAFT',
        scheduledStart: scheduledStart ?? undefined,
        scheduledEnd: scheduledEnd ?? undefined,
      }).unwrap();

      appDispatch(
        showToast({
          title: t('campaignUpdatedSuccessfully'),
          type: ToastType.SUCCESS,
        })
      );
      exit();
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('campaignUpdateFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const handleCreateAndSend = async (sendNow: boolean) => {
    const instance = instances?.find((inst) => inst.connectorId === connectorId);
    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }
    const { scheduledStart, scheduledEnd } = buildScheduleIso();
    const payload = {
      ...buildFlowInstancePayload({
        workspaceId,
        connectorFlowId,
        connectorInstanceId: instance.id,
        audienceId,
        flowParams,
        flowMappings,
      }),
    };
    try {
      const flowInstance = await createFlowInstance(payload).unwrap();
      const draftCampaign = await createCampaignV2({
        workspaceId,
        name: campaignName,
        description: campaignDescription,
        status: sendNow ? 'ACTIVE' : 'DRAFT',
        providerType: 'SMS',
        audienceId,
        connectorInstanceId: instance.id,
        scheduledStart,
        scheduledEnd,
      }).unwrap();

      await updateCampaignV2({
        workspaceId,
        campaignId: draftCampaign.id,
        status: sendNow ? 'ACTIVE' : 'DRAFT',
        connectorFlowInstanceId: flowInstance.id,
      }).unwrap();

      appDispatch(
        showToast({
          title: t('campaignCreatedAndActivatedSuccessfully'),
          type: ToastType.SUCCESS,
        })
      );
      exit();
      reset();
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const handlePreviewConfirmAndSend = isEditMode ? handleEditSave : handleCreateAndSend;

  if (isEditMode && isLoadingCampaign) {
    return <div className='rounded-2xl border border-gray-200 bg-white-100 p-8 text-sm text-gray-600 shadow-sm'>{t('loadingCampaign')}</div>;
  }

  if (isEditMode && (isCampaignError || (!isLoadingCampaign && !campaign))) {
    return <div className='rounded-2xl border border-red-200 bg-red-50 p-8 text-sm text-red-700 shadow-sm'>{t('campaignNotFound')}</div>;
  }

  return (
    <ErrorBoundary>
      <div className='flex w-full flex-col gap-10'>
        <div className='rounded-2xl border border-gray-200 bg-white-100 p-6 shadow-sm'>
          <CreateStepper steps={stepperSteps} currentStepIndex={state.currentStepIndex} />
        </div>
        <ErrorBoundary>
          <div className='rounded-2xl border border-gray-200 bg-white-100 p-8 shadow-sm'>
            {stepError && <div className='mb-6 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{stepError}</div>}
            {currentStep.id === 'TitleDescription' && (
              <ErrorBoundary>
                <TitleDescriptionStep onNext={handleTitleDescriptionNextStep} onCancel={onExitToList} />
              </ErrorBoundary>
            )}
            {currentStep.id === 'AudienceSelection' && (
              <ErrorBoundary>
                <AudienceSelectionStep handleBack={handlePreviousStep} handleNext={handleAudienceNextStep} workspaceId={workspaceId} />
              </ErrorBoundary>
            )}
            {currentStep.id === 'SenderSelection' && (
              <ErrorBoundary>
                <SenderSelectionStep onBack={handlePreviousStep} onNext={handleSenderNextStep} />
              </ErrorBoundary>
            )}

            {currentStep.id === 'PreviewSchedule' && (
              <PreviewScheduleStep
                onBack={handlePreviousStep}
                formValues={formValues}
                onConfirmAndSend={handlePreviewConfirmAndSend}
                isSubmitting={isCreatingCampaign || isUpdatingCampaign}
                mode={mode}
              />
            )}
          </div>
        </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
};
